/**
 * Phone booking activity — what the AI line actually did, including the calls it lost.
 *
 * WHY THIS EXISTS
 * ---------------
 * A successful phone booking already appears in the diary. A REJECTED one used to leave
 * nothing behind at all: a customer rang, was turned away, and the workshop never found
 * out. That is lost revenue that was invisible by construction. This drawer is the record
 * of both halves, and "Missed" is deliberately the first thing shown.
 *
 * Missed callers are actionable, so the caller's number is always displayed and is a
 * tel: link — the whole point is that someone rings them back.
 *
 * `spokenMessage` is exactly what the bot said. Showing it matters for trust: a workshop
 * that cannot audit what the bot told a customer will not leave it switched on.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { getVoiceActivityApi } from '../services/voiceBookingApi';
import VoiceCorrectionNote from './VoiceCorrectionNote';

/** Outcome → how it reads to a workshop, not how it reads to a developer. */
const OUTCOME_LABELS = {
    CREATED:               'Booked',
    WORKSHOP_CLOSED:       'Closed that day',
    OUTSIDE_WORKING_HOURS: 'Outside hours',
    SERVICE_NOT_BOOKABLE:  'Service not offered by phone',
    INVALID_BOOKING:       'Details incomplete',
    INVALID_DATE:          'Date not understood',
    DATE_IN_PAST:          'Date already passed',
    NUMBER_NOT_ASSIGNED:   'Number not assigned',
    BOT_DISABLED:          'Bot switched off',
    MISSING_DIALED_NUMBER: 'Call misrouted',
    BOOKING_FAILED:        'System error',
    ERROR:                 'System error',
};

const FILTERS = [
    { key: 'missed', label: 'Missed' },
    { key: 'booked', label: 'Booked' },
    { key: 'all',    label: 'All' },
];

const formatWhen = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString(undefined, {
        day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
    });
};

const formatDate = (iso) => {
    if (!iso) return null;
    const d = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
};

const Stat = ({ label, value, tone }) => (
    <div className="flex-1 rounded-lg border border-gray-200 bg-white p-3">
        <div className={`text-2xl font-semibold ${tone}`}>{value}</div>
        <div className="text-xs text-gray-500">{label}</div>
    </div>
);

/*
 * Five, not ten. A caller describing a fault — "there's a grinding noise when I brake and
 * it pulls left" — makes a row several lines tall, and the caller's words are deliberately
 * NOT truncated because that sentence is the reason the card exists. Ten of those does not
 * fit a laptop drawer without heavy scrolling.
 */
const PAGE_SIZE = 5;

/** ISO date `n` days ago — the default window start. */
const daysAgo = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().slice(0, 10);
};
const todayIso = () => new Date().toISOString().slice(0, 10);

const PhoneBookingsDrawer = ({ open, onClose }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('missed');

    /*
     * Explicit date window rather than a fixed "last 30 days". A workshop chasing one
     * specific call should be able to go straight to the day, not page through a month.
     * Defaults to the last 30 days so the drawer still opens with something useful.
     */
    const [from, setFrom] = useState(daysAgo(30));
    const [to, setTo] = useState(todayIso());
    const [page, setPage] = useState(0);

    const load = useCallback(async (opts = {}) => {
        const nextPage = opts.page ?? 0;
        setLoading(true);
        setError(null);
        try {
            setData(await getVoiceActivityApi({
                from: opts.from ?? from,
                to: opts.to ?? to,
                page: nextPage,
                size: PAGE_SIZE,
            }));
            setPage(nextPage);
        } catch (e) {
            // A workshop with the bot switched off should not see a scary error.
            setError(e?.response?.status === 404
                ? 'Phone bookings are not set up for this workshop yet.'
                : 'Could not load phone booking activity.');
        } finally {
            setLoading(false);
        }
    }, [from, to]);

    // Refetch on every open rather than caching: a workshop opens this precisely because
    // it wants to know whether anything came in just now.
    useEffect(() => {
        if (open) load();
    }, [open, load]);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    const attempts = data?.attempts ?? [];
    const shown = attempts.filter((a) => {
        if (filter === 'booked') return a.outcome === 'CREATED';
        if (filter === 'missed') return a.outcome !== 'CREATED';
        return true;
    });

    return (
        <div className="fixed inset-0 z-50 flex justify-end">
            <div
                className="absolute inset-0 bg-black/30"
                onClick={onClose}
                aria-hidden="true"
            />

            <aside
                role="dialog"
                aria-label="Phone booking activity"
                className="relative flex h-full w-full max-w-md flex-col bg-gray-50 shadow-xl"
            >
                <header className="flex items-start justify-between border-b border-gray-200 bg-white px-4 py-3">
                    <div>
                        <h2 className="text-base font-semibold text-gray-900">Phone bookings</h2>
                        <p className="text-xs text-gray-500">AI booking line</p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                        aria-label="Close"
                    >
                        ✕
                    </button>
                </header>

                {data && (
                    <div className="flex gap-2 px-4 pt-3">
                        <Stat label="Calls"  value={data.callsInWindow}  tone="text-gray-900" />
                        <Stat label="Booked" value={data.bookedInWindow} tone="text-green-600" />
                        <Stat
                            label="Missed"
                            value={data.missedInWindow}
                            tone={data.missedInWindow > 0 ? 'text-amber-600' : 'text-gray-900'}
                        />
                    </div>
                )}

                {/* Explicit window. Changing either date reloads from page 0 — page 4 of a
                    different range is meaningless. */}
                <div className="flex items-end gap-2 px-4 pt-3">
                    <label className="flex-1">
                        <span className="mb-0.5 block text-[11px] font-medium text-gray-500">From</span>
                        <input
                            type="date"
                            value={from}
                            max={to}
                            onChange={(e) => { setFrom(e.target.value); load({ from: e.target.value, page: 0 }); }}
                            className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500"
                        />
                    </label>
                    <label className="flex-1">
                        <span className="mb-0.5 block text-[11px] font-medium text-gray-500">To</span>
                        <input
                            type="date"
                            value={to}
                            min={from}
                            max={todayIso()}
                            onChange={(e) => { setTo(e.target.value); load({ to: e.target.value, page: 0 }); }}
                            className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500"
                        />
                    </label>
                </div>

                <div className="flex gap-1 px-4 py-3">
                    {FILTERS.map(({ key, label }) => (
                        <button
                            key={key}
                            type="button"
                            onClick={() => setFilter(key)}
                            className={`rounded-md px-3 py-1 text-sm ${
                                filter === key
                                    ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                                    : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                    <button
                        type="button"
                        onClick={() => load({ page })}
                        disabled={loading}
                        className="ml-auto rounded-md px-3 py-1 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
                    >
                        {loading ? 'Refreshing…' : 'Refresh'}
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-4 pb-4">
                    {error && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                            {error}
                        </div>
                    )}

                    {!error && loading && !data && (
                        <p className="py-8 text-center text-sm text-gray-500">Loading…</p>
                    )}

                    {!error && !loading && shown.length === 0 && (
                        <p className="py-8 text-center text-sm text-gray-500">
                            {filter === 'missed'
                                ? 'No missed calls. Every caller got a booking.'
                                : 'Nothing here yet.'}
                        </p>
                    )}

                    <ul className="space-y-2">
                        {shown.map((a) => {
                            const booked = a.outcome === 'CREATED';
                            return (
                                <li
                                    key={a.uuid}
                                    className={`rounded-lg border bg-white p-3 ${
                                        a.recoverable ? 'border-amber-200' : 'border-gray-200'
                                    }`}
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <span
                                            className={`rounded px-2 py-0.5 text-xs font-medium ${
                                                booked
                                                    ? 'bg-green-100 text-green-800'
                                                    : a.recoverable
                                                        ? 'bg-amber-100 text-amber-800'
                                                        : 'bg-gray-100 text-gray-600'
                                            }`}
                                        >
                                            {OUTCOME_LABELS[a.outcome] ?? a.outcome}
                                        </span>
                                        <span className="text-xs text-gray-400">
                                            {formatWhen(a.occurredAt)}
                                        </span>
                                    </div>

                                    <div className="mt-2 text-sm text-gray-900">
                                        {a.customerName || 'Unknown caller'}
                                        {a.vehicleRego && (
                                            <span className="text-gray-500"> · {a.vehicleRego}</span>
                                        )}
                                    </div>

                                    <div className="mt-0.5 text-xs text-gray-600">
                                        {a.spokenService || 'No service given'}
                                        {a.requestedDate && <> · wanted {formatDate(a.requestedDate)}</>}
                                    </div>

                                    {/* The point of a missed call is that someone rings back. */}
                                    {a.callerNumber && (
                                        <a
                                            href={`tel:${a.callerNumber}`}
                                            className="mt-2 inline-block text-sm font-medium text-blue-600 hover:underline"
                                        >
                                            {a.callerNumber}
                                        </a>
                                    )}

                                    {booked && a.bookingCode && (
                                        <div className="mt-1 text-xs text-gray-500">
                                            Ref {a.bookingCode}
                                        </div>
                                    )}

                                    {/* The CALLER's words first — that is what someone
                                        ringing back needs. The bot's own line is the same
                                        sentence on every row, so it is secondary. */}
                                    {(a.callerServiceWords || a.lastUtterance) && (
                                        <div className="mt-2 rounded-md bg-gray-50 px-2 py-1.5">
                                            <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
                                                Caller said
                                            </p>
                                            <p className="text-xs text-gray-700">
                                                “{a.callerServiceWords || a.lastUtterance}”
                                            </p>
                                        </div>
                                    )}

                                    <VoiceCorrectionNote item={a} />

                                    {a.spokenMessage && (
                                        <p className="mt-1.5 border-l-2 border-gray-200 pl-2 text-[11px] italic text-gray-400">
                                            Bot: “{a.spokenMessage}”
                                        </p>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>

                {/* Pinned below the scroll area so paging never requires scrolling back up
                    — the case that matters on a laptop where the drawer is already tight. */}
                {(data?.totalPages ?? 0) > 1 && (
                    <footer className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-2">
                        <button
                            type="button"
                            onClick={() => load({ page: page - 1 })}
                            disabled={page === 0 || loading}
                            className="rounded px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-40"
                        >
                            ‹ Prev
                        </button>
                        <span className="text-xs text-gray-500">
                            Page {page + 1} of {data.totalPages} · {data.totalItems} calls
                        </span>
                        <button
                            type="button"
                            onClick={() => load({ page: page + 1 })}
                            disabled={page + 1 >= (data?.totalPages ?? 0) || loading}
                            className="rounded px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-40"
                        >
                            Next ›
                        </button>
                    </footer>
                )}
            </aside>
        </div>
    );
};

export default PhoneBookingsDrawer;
